import { useState, useEffect } from "react";
import { X, Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { TOAST } from "@/lib/toastMessages";
import { motion, AnimatePresence } from "framer-motion";

export function EmailVerificationBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkVerification = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Check if email is confirmed
      const isConfirmed = user.email_confirmed_at !== null;
      
      // Check if user has dismissed this banner before
      const dismissedKey = `email_verification_dismissed_${user.id}`;
      const wasDismissed = localStorage.getItem(dismissedKey) === "true";
      
      if (!isConfirmed && !wasDismissed) {
        setEmail(user.email || null);
        setShowBanner(true);
      }
    };

    checkVerification();
  }, []);

  const handleResendVerification = async () => {
    if (!email) return;
    
    setSending(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });
      
      if (error) throw error;
      
      toast.success(TOAST.EMAIL.VERIFICATION_SENT.title);
    } catch (error: any) {
      toast.error(TOAST.EMAIL.VERIFICATION_ERROR(error.message).title);
    } finally {
      setSending(false);
    }
  };

  const handleDismiss = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      localStorage.setItem(`email_verification_dismissed_${user.id}`, "true");
    }
    setDismissed(true);
    setTimeout(() => setShowBanner(false), 300);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="bg-primary/10 border-b border-primary/20"
        >
          <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-sm">
              <Mail className="h-4 w-4 text-primary shrink-0" />
              <span className="text-foreground/80">
                Verify your email to secure your account
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResendVerification}
                disabled={sending}
                className="text-xs h-7"
              >
                {sending ? "Sending..." : "Send verification email"}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDismiss}
                className="h-7 w-7 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
